import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { Button } from "../components/ui/button";
import { Check, Loader2, ArrowRight, Info } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const FREQ = { monthly: "/month", annual: "/year", quarterly: "/quarter" };

export default function Membership() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [intro, setIntro] = useState({ headline: "Choose Your Next Adventure.", subtext: "" });
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/membership-plans"),
      api.get("/site-content"),
    ]).then(([p, c]) => {
      setPlans(p.data);
      const cc = c.data || {};
      if (cc.memberships_intro) setIntro(cc.memberships_intro);
      setPricing(cc.experience_pricing || {});
    }).finally(() => setLoading(false));
  }, []);

  const choose = async (plan) => {
    if (!user) { navigate("/register"); return; }
    setBusy(plan.id);
    try {
      const { data } = await api.post("/membership/subscribe", { plan_id: plan.id, origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else { toast.success("Membership activated!"); navigate("/dashboard"); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const signature = [
    { key: "day_explorer", tag: "NONMEMBER", title: "H.E.A. Day Explorer", price: pricing?.day_explorer, sub: "per trip", desc: pricing?.day_explorer_desc, cta: "Explore Day Experiences", to: "/experiences" },
    { key: "signature_member", tag: "MEMBER", title: "Signature Experience — Member", price: pricing?.signature_member, sub: "per trip", desc: pricing?.signature_member_desc, cta: "View Signature Experiences", to: "/experiences" },
    { key: "signature_guest", tag: "GUEST", title: "Signature Experience — Guest", price: pricing?.signature_guest, sub: "per trip", desc: pricing?.signature_guest_desc, cta: "View Signature Experiences", to: "/experiences" },
  ];

  const sigRate = pricing?.signature_member ?? 249;

  return (
    <PublicLayout>
      <section className="bg-navy topo-bg">
        <div className="mx-auto max-w-7xl px-5 py-16 text-center lg:px-8">
          <span className="eyebrow text-gold">Memberships & Pricing</span>
          <h1 className="mt-3 font-display text-4xl font-extrabold text-white lg:text-5xl">{intro.headline}</h1>
          <p className="mx-auto mt-4 max-w-2xl text-white/70">{intro.subtext}</p>
        </div>
      </section>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brandblue" /></div> : (
      <>
        {/* MEMBERSHIP CARDS */}
        <section className="mx-auto max-w-6xl px-5 py-16 lg:px-8">
          <div className="grid gap-8 md:grid-cols-3">
            {plans.map((p) => {
              const core = p.category === "CORE";
              return (
                <div key={p.id} data-testid={`membership-plan-${p.id}`} className={`relative flex flex-col rounded-3xl border bg-white p-8 ${core ? "border-brandblue shadow-xl md:-mt-4" : "border-slate-200"}`}>
                  {core && <span className="absolute -top-3 left-8 rounded-full bg-brandblue px-3 py-1 text-xs font-bold text-white">CORE MEMBERSHIP</span>}
                  <span className="eyebrow text-brandblue">{p.category}</span>
                  <h3 className="mt-2 font-display text-2xl font-bold text-navy">{p.name}</h3>
                  <div className="mt-4 font-display text-4xl font-extrabold text-navy">${p.price}<span className="text-base font-semibold text-slateBrand">{FREQ[p.billing_frequency] || ""}</span></div>
                  <div className="mt-1 text-sm font-semibold text-brandblue">{p.trips_label}</div>
                  <p className="mt-4 text-sm text-slateBrand">{p.description}</p>
                  <ul className="mt-6 flex-1 space-y-3">
                    {p.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm text-slateBrand"><Check className="mt-0.5 h-4 w-4 shrink-0 text-successBrand" />{b}</li>
                    ))}
                  </ul>
                  <Button data-testid={`membership-choose-${p.id}`} disabled={busy === p.id} onClick={() => choose(p)}
                    className="mt-8 h-12 w-full bg-brandblue font-display font-semibold hover:bg-blue-700">
                    {busy === p.id ? <Loader2 className="h-5 w-5 animate-spin" /> : `Choose ${p.name}`}
                  </Button>
                </div>
              );
            })}
          </div>
          <div className="mt-8 flex items-start gap-3 rounded-xl bg-cloud p-4 text-sm text-slateBrand">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-brandblue" />
            All regularly scheduled local trips are subject to capacity and advance registration. Premium destination (Signature) experiences are separate purchases and are not included in any monthly membership.
          </div>
        </section>

        {/* INDIVIDUAL & PREMIUM */}
        <section className="border-y border-slate-200 bg-cloud">
          <div className="mx-auto max-w-6xl px-5 py-16 lg:px-8">
            <div className="text-center">
              <span className="eyebrow text-brandblue">Pay As You Go</span>
              <h2 className="mt-2 font-display text-3xl font-bold text-navy lg:text-4xl">Explore At Your Own Pace.</h2>
              <p className="mx-auto mt-3 max-w-2xl text-slateBrand">Not ready for a monthly membership? Join individual educational experiences or explore our premium destination opportunities.</p>
            </div>
            <div className="mt-12 grid gap-8 md:grid-cols-3">
              {signature.map((s) => (
                <div key={s.key} data-testid={`pricing-${s.key}`} className="flex flex-col rounded-3xl border border-slate-200 bg-white p-8">
                  <span className="eyebrow text-gold">{s.tag}</span>
                  <h3 className="mt-2 font-display text-xl font-bold text-navy">{s.title}</h3>
                  <div className="mt-3 font-display text-3xl font-extrabold text-navy">${s.price}<span className="text-sm font-semibold text-slateBrand"> / {s.sub}</span></div>
                  <p className="mt-4 flex-1 text-sm text-slateBrand">{s.desc}</p>
                  <Button data-testid={`pricing-cta-${s.key}`} variant="outline" onClick={() => navigate(s.to)} className="mt-6 font-display font-semibold">
                    {s.cta} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-start gap-3 rounded-xl bg-white p-4 text-sm text-slateBrand">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-brandblue" />
              Signature Experiences are separate purchases from monthly memberships. The member rate is available to eligible H.E.A. members. All experiences require advance registration and are subject to capacity.
            </div>
          </div>
        </section>

        {/* COMPARISON TABLE */}
        <section className="mx-auto max-w-5xl px-5 py-16 lg:px-8">
          <h2 className="text-center font-display text-3xl font-bold text-navy">Compare Memberships</h2>
          <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full min-w-[560px] text-sm" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-slate-200 bg-cloud">
                  <th className="p-4 text-left font-display font-bold text-navy">Feature</th>
                  <th className="p-4 text-center font-display font-bold text-navy">Explorer</th>
                  <th className="p-4 text-center font-display font-bold text-navy">Discoverer</th>
                  <th className="p-4 text-center font-display font-bold text-navy">Trailblazer</th>
                </tr>
              </thead>
              <tbody className="text-slateBrand">
                {[
                  ["Monthly Price", "$79", "$149", "$199"],
                  ["Included Local Trips", "1", "2", "All Standard Trips"],
                  ["Hands-On Learning", "Included", "Included", "Included"],
                  ["Guided Exploration", "Included", "Included", "Included"],
                  ["Premium Destination Trips", "Separate", "Separate", "Separate"],
                  ["Member Signature Experience Rate", `$${sigRate}`, `$${sigRate}`, `$${sigRate}`],
                ].map((row, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="p-4 font-semibold text-navy">{row[0]}</td>
                    {row.slice(1).map((c, j) => <td key={j} className="p-4 text-center">{c === "Included" ? <Check className="mx-auto h-4 w-4 text-successBrand" /> : c}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 rounded-xl bg-cloud p-5 text-center text-sm text-slateBrand">
            Prefer to attend a single local experience? Individual local experiences are available to nonmembers for <span className="font-semibold text-navy">${pricing?.day_explorer ?? 69} per trip</span> through H.E.A. Day Explorer.
          </div>
        </section>
      </>
      )}
    </PublicLayout>
  );
}
